import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';

import { ChatBubbleOvalLeft, StarSolidIcon } from '../Icon';
import { getReviews } from '@/api/public';
import { colors } from '@/const/colors';
import { maskTruckNumber } from '@/utils/common';

const SCROLL_INTERVAL_MS = 5000;
const TRANSITION = '600ms cubic-bezier(0.22, 1, 0.36, 1)';
const META_ROW_HEIGHT = 14;
const STAR_COUNT = 5;
const STAR_SIZE = 14;
const STAR_ACTIVE_COLOR = '#FFC900';

// 별점은 문자열로 내려오고, 0.5점 단위로만 노출한다.
const toStarRating = (value?: string | number | null) => {
  const rating = Number(value);
  if (!Number.isFinite(rating) || rating <= 0) {
    return 0;
  }

  return Math.min(Math.round(rating * 2) / 2, STAR_COUNT);
};

const StarRating = ({ rating }: { rating: number }) => {
  if (rating === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-[2px]">
      {Array.from({ length: STAR_COUNT }, (_, starIndex) => {
        const fillRatio = Math.min(Math.max(rating - starIndex, 0), 1);

        return (
          <div key={starIndex} className="relative shrink-0" style={{ width: STAR_SIZE, height: STAR_SIZE }}>
            <StarSolidIcon className="block" color={colors.gray[4]} width={STAR_SIZE} height={STAR_SIZE} />
            {/* 반 개는 채운 별을 가로로 잘라서 표현한다. */}
            {fillRatio > 0 && (
              <div className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${fillRatio * 100}%` }}>
                <StarSolidIcon className="block" color={STAR_ACTIVE_COLOR} width={STAR_SIZE} height={STAR_SIZE} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// 차량번호와 별점도 후기와 같은 타이밍으로 함께 흐르게 한다.
const MetaColumn = ({
  offset,
  transition,
  className = '',
  children,
}: {
  offset: number;
  transition: string;
  className?: string;
  children: React.ReactNode;
}) => (
  <div className={`overflow-hidden ${className}`} style={{ height: META_ROW_HEIGHT }}>
    <div style={{ transform: `translateY(-${offset}px)`, transition }}>{children}</div>
  </div>
);

const ReviewTicker = () => {
  const { data: reviews = [] } = useQuery<ProductReview[]>('get-reviews', getReviews, {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
  });

  const validReviews = useMemo(() => reviews.filter((review) => Boolean(review.productReview?.trim())), [reviews]);

  // 마지막 후기에서 첫 후기로 이어서 흐르도록 첫 후기를 한 번 더 붙인다.
  const tickerReviews = validReviews.length > 1 ? [...validReviews, validReviews[0]] : validReviews;

  const [index, setIndex] = useState(0);
  const [enableTransition, setEnableTransition] = useState(true);
  const [itemHeights, setItemHeights] = useState<number[]>([]);

  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLParagraphElement | null>>([]);

  // 후기마다 줄 수가 달라지므로 실제 높이를 재서 이동 거리와 노출 높이를 맞춘다.
  const measureItems = useCallback(() => {
    const heights = itemRefs.current
      .slice(0, tickerReviews.length)
      .map((element) => (element ? element.getBoundingClientRect().height : 0));

    setItemHeights((prev) =>
      prev.length === heights.length && prev.every((height, i) => Math.abs(height - heights[i]) < 0.5)
        ? prev
        : heights,
    );
  }, [tickerReviews.length]);

  useLayoutEffect(() => {
    measureItems();
  }, [measureItems, validReviews]);

  // 화면 폭이 바뀌면 줄 수도 달라지므로 다시 측정한다.
  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(measureItems);
    observer.observe(list);

    return () => observer.disconnect();
  }, [measureItems, validReviews]);

  useEffect(() => {
    if (validReviews.length <= 1) {
      return undefined;
    }

    // 복제된 첫 후기를 지나치지 않게 막아 되돌리기 전까지 대기시킨다.
    const intervalId = window.setInterval(
      () => setIndex((prev) => (prev >= validReviews.length ? prev : prev + 1)),
      SCROLL_INTERVAL_MS,
    );

    return () => window.clearInterval(intervalId);
  }, [validReviews.length]);

  useEffect(() => {
    if (enableTransition) {
      return undefined;
    }

    const frameId = requestAnimationFrame(() => setEnableTransition(true));
    return () => cancelAnimationFrame(frameId);
  }, [enableTransition]);

  // 복제된 첫 후기까지 흐른 뒤에는 애니메이션 없이 처음으로 되돌린다.
  const handleTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName === 'transform' && index >= validReviews.length) {
      setEnableTransition(false);
      setIndex(0);
    }
  };

  if (validReviews.length === 0) {
    return null;
  }

  const activeIndex = Math.min(index, tickerReviews.length - 1);
  const isMeasured = itemHeights.length === tickerReviews.length;
  const offset = isMeasured ? itemHeights.slice(0, activeIndex).reduce((sum, height) => sum + height, 0) : 0;
  const scrollTransition = enableTransition ? `transform ${TRANSITION}` : 'none';
  const metaOffset = activeIndex * META_ROW_HEIGHT;

  return (
    <div className="px-4 pt-[2px] pb-[18px]">
      <div className="relative flex items-start gap-3 overflow-hidden rounded-[12px] bg-gray-0 py-4 pl-[19px] pr-4 shadow-soft">
        <span className="absolute inset-y-0 left-0 w-[3px] bg-blueGradient-blue-to-blue" />
        <div className="flex shrink-0 items-center justify-center w-[34px] h-[34px] rounded-full bg-primary-1">
          <ChatBubbleOvalLeft color={colors.primary[10]} width={19} height={19} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-[6px]">
            <p className="shrink-0 text-[11px] font-semibold leading-[14px] tracking-[0.2px] text-primary-10">
              실제 거래 후기
            </p>
            <MetaColumn offset={metaOffset} transition={scrollTransition} className="flex-1 min-w-0">
              {tickerReviews.map((review, itemIndex) => (
                <p
                  key={`${itemIndex}-${review.id}`}
                  className="text-[11px] font-medium leading-[14px] text-gray-6 truncate"
                  style={{ height: META_ROW_HEIGHT }}
                >
                  {maskTruckNumber(review.truckNumber)}
                </p>
              ))}
            </MetaColumn>
            <MetaColumn offset={metaOffset} transition={scrollTransition} className="shrink-0">
              {tickerReviews.map((review, itemIndex) => (
                <div
                  key={`${itemIndex}-${review.id}`}
                  className="flex items-center justify-end"
                  style={{ height: META_ROW_HEIGHT }}
                >
                  <StarRating rating={toStarRating(review.productStarNumber)} />
                </div>
              ))}
            </MetaColumn>
          </div>
          <div
            className="mt-[5px] overflow-hidden"
            style={{
              height: isMeasured ? itemHeights[activeIndex] : undefined,
              transition: enableTransition ? `height ${TRANSITION}` : 'none',
            }}
          >
            <div
              ref={listRef}
              style={{ transform: `translateY(-${offset}px)`, transition: scrollTransition }}
              onTransitionEnd={handleTransitionEnd}
            >
              {tickerReviews.map((review, itemIndex) => (
                <p
                  key={`${itemIndex}-${review.id}`}
                  ref={(element) => {
                    itemRefs.current[itemIndex] = element;
                  }}
                  className="text-[14px] font-medium leading-[20px] text-gray-8 break-keep"
                >
                  {review.productReview}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewTicker;
